import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../modules/auth/services/auth.service';
import { TranslationService } from '../../modules/i18n/translation.service';
import { AlertMessageService } from '../../modules/shared/services/alert-message.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

export interface LogoutOptions {
  showConfirmation?: boolean;
  redirectTo?: string;
  preserveLanguage?: boolean;
}

const DEFAULT_LOGOUT_OPTIONS: LogoutOptions = {
  showConfirmation: true,
  redirectTo: '/auth/login',
  preserveLanguage: true
};

@Injectable({
  providedIn: 'root'
})
export class LogoutService {

  constructor(
    private authService: AuthService,
    private router: Router,
    private alertService: AlertMessageService,
    private translateService: TranslateService
  ) { }

  /**
   * Realiza el logout con opciones configurables usando el mismo estilo de alerta del sistema
   * @param options Opciones para personalizar el comportamiento del logout
   * @returns Observable<boolean> - true si el logout fue exitoso
   */
  logout(options: LogoutOptions = {}): Observable<boolean> {
    const config = { ...DEFAULT_LOGOUT_OPTIONS, ...options };

    if (config.showConfirmation) {
      return this.showConfirmationAndLogout(config);
    } else {
      return this.performLogout(config);
    }
  }

  /**
   * Logout silencioso sin confirmaciones ni mensajes
   * @param redirectTo Ruta a la que redirigir después del logout
   */
  silentLogout(redirectTo: string = '/auth/login'): void {
    this.logout({
      showConfirmation: false,
      redirectTo
    }).subscribe();
  }

  /**
   * Logout de emergencia - limpia todo localmente y redirige
   * @param redirectTo Ruta a la que redirigir
   */
  emergencyLogout(redirectTo: string = '/auth/login'): void {
    this.clearLocalData(true);
    
    this.alertService.showMessage(
      this.translateService.instant('GENERAL.SESSION_EXPIRED'),
      this.translateService.instant('GENERAL.ON_SESSION_EXPIRED_MESSAGE'),
      'warning'
    ).then(() => {
      this.router.navigate([redirectTo]);
    });
  }

  private showConfirmationAndLogout(config: LogoutOptions): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      // Usar el mismo servicio de alertas que usa el sidebar
      this.alertService.showQuestion(
        this.translateService.instant('LOGOUT.MESSAGE'),
        this.translateService.instant('LOGOUT.CONFIRMATION.MESSAGE'),
        this.translateService.instant('GENERAL.YES'),
        this.translateService.instant('GENERAL.NO')
      ).then((result) => {
        if (result.isConfirmed) {
          this.performLogout(config).subscribe({
            next: (success) => observer.next(success),
            error: (error) => observer.error(error),
            complete: () => observer.complete()
          });
        } else {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  private performLogout(config: LogoutOptions): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      // Realizar logout a través del servicio de autenticación
      this.authService.logout().pipe(
        catchError((error) => {
          console.error('Logout error:', error);
          return of(false); // Continuar con el flujo aunque falle
        }),
        finalize(() => {
          // Siempre limpiar datos locales
          this.clearLocalData(config.preserveLanguage);
          // Navegar después de limpiar
          this.navigateToRoute(config.redirectTo!);
        })
      ).subscribe({
        next: (success) => {
          observer.next(!!success); // Convertir a boolean
          observer.complete();
        },
        error: (error) => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  private clearLocalData(preserveLanguage: boolean = true): void {
    try {
      // Mantener datos que deben persistir
      const dataToPreserve: { [key: string]: string | null } = {};
      
      if (preserveLanguage) {
        dataToPreserve.language = localStorage.getItem('language');
      }
      
      // Limpiar sessionStorage completamente
      sessionStorage.clear();
      
      // Limpiar localStorage específicos
      const keysToRemove = [
        'practiceId', 
        'practiceName', 
        'loginMethod', 
        'resendOtpDelay',
        // Agregar aquí más keys según sea necesario
      ];
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Restaurar datos preservados
      Object.entries(dataToPreserve).forEach(([key, value]) => {
        if (value !== null) {
          localStorage.setItem(key, value);
        }
      });
      
    } catch (error) {
      console.error('Error clearing local data:', error);
    }
  }

  private navigateToRoute(route: string): void {
    this.router.navigate([route]).catch((error) => {
      console.error('Navigation error after logout:', error);
      // Fallback a la ruta raíz si falla la navegación
      window.location.href = route;
    });
  }
}
